import { Persona } from "@/schemas/firestore/persona.schema";
import { useStore } from "@/store";

export interface PersonasResult {
  // Persona State
  personas: Persona[];
  selectedPersonaId: string | null;
  isLoading: boolean;
  error: string | null;

  // Persona Actions
  subscribeToPersonas: (userId: string, domainId: string) => () => void;
  setSelectedPersona: (personaId: string | null) => void;
  setPersonaEnabled: (
    domainId: string,
    personaId: string,
    isEnabled: boolean,
  ) => Promise<void>;
  clearPersonas: () => void;
  unsubscribePersonas: () => void;

  // Computed values
  selectedPersona: Persona | null;
}

/**
 * Custom hook to access and manage persona-related data and operations
 * @param domainId - Optional domain ID to filter personas
 */
export const usePersonas = (domainId?: string): PersonasResult => {
  const {
    // Persona state
    personas: allPersonas,
    selectedPersonaId,
    isLoading,
    error,

    // Persona actions
    subscribeToPersonas,
    setSelectedPersona,
    setPersonaEnabled,
    clearPersonas,
    unsubscribePersonas,
  } = useStore();

  // Filter personas by domain if domainId is provided
  const personas = domainId
    ? allPersonas.filter((p) => p.domain_id === domainId)
    : allPersonas;

  // Computed values
  const selectedPersona =
    personas.find((p) => p.id === selectedPersonaId) || null;

  return {
    // Persona state
    personas,
    selectedPersonaId,
    isLoading,
    error,

    // Persona actions
    subscribeToPersonas,
    setSelectedPersona,
    setPersonaEnabled,
    clearPersonas,
    unsubscribePersonas,

    // Computed values
    selectedPersona,
  };
};
